import React from "react";
import { Modal, notification } from "antd";
import { useParams } from "react-router-dom";
import GistEditorForm from "../../shared/components/GistEditorForm";

type Props = {
  setShowModal: (value: boolean) => void;
  gist_id: number;
};

/**
 * ReplyModal creates a reply-gist for the currently visible parent gist.
 *
 * Important: reply-gists must use the same atomic creation path as normal gists.
 * The old 3-step CREATE_GIST -> CREATE_VERSION -> CREATE_EDIT flow could not
 * support draft/published status consistently and could leave partial objects
 * behind if one step failed.
 */
const ReplyModal: React.FC<Props> = ({ setShowModal, gist_id }) => {
  const { id: currentSubjectId } = useParams<{ id?: string }>();
  const subjectId = currentSubjectId ? parseInt(currentSubjectId, 10) : 0;

  const closeModal = () => setShowModal(false);

  const handleSuccess = () => {
    notification.success({
      message: "Reply gist saved",
      description: "Your reply gist was saved successfully.",
    });
    closeModal();
  };

  return (
    <Modal
      title="Create reply gist"
      open={true}
      onCancel={closeModal}
      footer={null}
      centered
      width={560}
      destroyOnClose
    >
      {!subjectId ? (
        <div style={{ color: "var(--color-charcoal)", fontSize: 14 }}>
          Cannot create a reply gist because the current subject could not be
          determined.
        </div>
      ) : (
        <>
          <p
            style={{
              marginTop: 0,
              marginBottom: 16,
              color: "var(--color-slate)",
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            Publish this reply gist now, or save it as a private draft and come
            back to it later.
          </p>

          <GistEditorForm
            subjectId={subjectId}
            parentGistId={gist_id}
            onSuccess={handleSuccess}
            onCancel={closeModal}
            publishedSubmitLabel="Publish"
            draftSubmitLabel="Save Draft"
            savingLabel="Saving..."
            draftOptionLabel="Save as private draft instead of publishing now"
          />
        </>
      )}
    </Modal>
  );
};

export default ReplyModal;
